import { Currency } from './CompanyApi';
import { User } from '@/types/users';
import { ApiQueryKey } from '@/configs/apiQueryKey';
import { ApiQueryOptions, CustomFilters, UUID } from '@/types/types';
import { InvestmentNote } from './InvestmentNoteApi';
import { InvestmentItem } from './InvestmentItemApi';
import { fileTypeToHeader } from '@/utils/utilities';
import EntityApi, { BaseEntity } from './EntityApi';
import httpClient from './httpClient';

export enum InvestmentStatus {
	NOT_SENT = 'NOT_SENT',
	SENT = 'SENT',
	CANCELLED = 'CANCELLED',
	IN_PROGRESS = 'IN_PROGRESS',
	COMPLETED = 'COMPLETED',
}

export interface ShippingInvestmentData {
	shippingName: string;
	shippingStreet: string;
	shippingCity: string;
	shippingPostalCode: string;
	shippingPhoneNumber: string;
}

export interface InvestmentDelivery extends BaseEntity {
	name: string;
	deliverDate: Date;
	investment: Investment;
	deliveryLines: InvestmentDeliveryLine[];
}

export interface InvestmentDeliveryLine extends BaseEntity {
	investmentDelivery: InvestmentDelivery;
	configurationArticleCode: string;
	quantity: number;
}

export interface Investment extends BaseEntity, ShippingInvestmentData {
	number: string;
	name: string;
	user: User;
	currency: Currency;
	investmentDate: Date;
	address: string;
	description: string;
	status: InvestmentStatus;
	isArchived: boolean;
	investmentItems: InvestmentItem[];
	investmentDeliveries: InvestmentDelivery[];
	notes: InvestmentNote[];
}

class InvestmentApi extends EntityApi<Investment> {
	constructor() {
		super(ApiQueryKey.investments);
		this.changeStatus = this.changeStatus.bind(this);
		this.addNote = this.addNote.bind(this);
		this.deleteNote = this.deleteNote.bind(this);
		this.exportToPDF = this.exportToPDF.bind(this);
		this.exportToXLSX = this.exportToXLSX.bind(this);
		this.exportStatsXLSX = this.exportStatsXLSX.bind(this);
	}

	async exportStatsXLSX({
		queryParams,
		customFilters,
	}: {
		queryParams: ApiQueryOptions<Investment>;
		customFilters: CustomFilters;
	}) {
		const params: Record<string, any> = {
			...queryParams,
			sortBy: `${queryParams.sortBy.key}:${queryParams.sortBy.order}`,
			search: queryParams.search?.trim(),
			...customFilters,
		};

		for (const key in params) {
			if (params[key] === '') delete params[key];
		}

		const response = await httpClient.get<Blob>(
			`${this.queryKey}/stats/export-xlsx`,
			{
				headers: {
					Accept: fileTypeToHeader('xlsx'),
				},
				params,
				responseType: 'blob',
			}
		);

		return response.data;
	}

	async changeStatus({
		investmentId,
		status,
	}: {
		investmentId: UUID;
		status: InvestmentStatus;
	}) {
		const response = await httpClient.patch<Investment>(
			`${this.queryKey}/${investmentId}/change-status`,
			{ status }
		);

		return response.data;
	}

	async addNote({
		investmentId,
		value,
	}: {
		investmentId: UUID;
		value: string;
	}) {
		await httpClient.post<InvestmentNote>(
			`${this.queryKey}/${investmentId}/add-note`,
			{ value }
		);

		return 1;
	}

	async deleteNote({
		investmentId,
		noteId,
	}: {
		investmentId: UUID;
		noteId: UUID;
	}) {
		await httpClient.delete(
			`${this.queryKey}/${investmentId}/delete-note/${noteId}`
		);

		return 1;
	}

	async exportToPDF(id: UUID) {
		const response = await httpClient.get<Blob>(
			`${this.queryKey}/${id}/export-pdf`,
			{
				headers: {
					Accept: fileTypeToHeader('pdf'),
				},
				responseType: 'blob',
			}
		);

		return response.data;
	}

	async exportToXLSX(id: UUID) {
		const response = await httpClient.get<Blob>(
			`${this.queryKey}/${id}/export-xlsx`,
			{
				headers: {
					Accept: fileTypeToHeader('xlsx'),
				},
				responseType: 'blob',
			}
		);

		return response.data;
	}
}

export default new InvestmentApi();
